import { useEffect, useState } from "react";

interface ModelData {
  id: string;
  model_id: string;
  display_name: string;
  provider: string;
  location: string;
  quality: number;
  speed: number;
  coding: number;
  reasoning: number;
  privacy: number;
  cost_per_1k_tokens_usd: number;
  est_response_cost_usd: number;
  tokens_per_second: number;
  context_window: number;
}

const COLORS: Record<string, string> = {
  local: "#10B981",
  edge: "#3B82F6",
  remote: "#F59E0B",
};

const LOCATION_LABELS: Record<string, string> = {
  local: "Local GPU",
  edge: "CF Edge",
  remote: "Remote API",
};

export function ModelCompare({ apiBase = "" }: { apiBase?: string }) {
  const [models, setModels] = useState<ModelData[]>([]);
  const [hover, setHover] = useState<string | null>(null);
  const [weights, setWeights] = useState({ quality: 50, speed: 50, cost: 50 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("infra_token");
    const headers: Record<string, string> = {};
    if (token) headers["authorization"] = `Bearer ${token}`;

    fetch(`${apiBase}/v1/models/compare`, {
      credentials: "include",
      headers,
    })
      .then((r) => r.json())
      .then((d) => {
        setModels(d.models || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [apiBase]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Loading models...
      </div>
    );
  }

  if (!models.length) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No models available
      </div>
    );
  }

  const scored = models.map((m) => {
    const costScore =
      m.cost_per_1k_tokens_usd === 0 ? 100 : Math.max(0, 100 - m.cost_per_1k_tokens_usd * 50000);
    const w =
      (m.quality * weights.quality + m.speed * weights.speed + costScore * weights.cost) /
      (weights.quality + weights.speed + weights.cost || 1);
    return { ...m, weightedScore: w, costScore };
  });
  const winner = scored.reduce((a, b) => (a.weightedScore > b.weightedScore ? a : b));

  const W = 480,
    H = 320,
    PAD = 48;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold text-foreground mb-4">Model Comparison</h1>

        {/* Priority sliders */}
        <div className="flex flex-wrap gap-4 mb-6">
          {(["quality", "speed", "cost"] as const).map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground capitalize w-12">{key}</span>
              <input
                type="range"
                min={0}
                max={100}
                value={weights[key]}
                onChange={(e) => setWeights((w) => ({ ...w, [key]: +e.target.value }))}
                className="w-20 h-1 bg-border rounded-full appearance-none cursor-pointer accent-primary"
              />
              <span className="font-mono text-foreground w-8 text-right">{weights[key]}</span>
            </label>
          ))}
        </div>

        {/* Scatter plot */}
        <div className="bg-card border border-border rounded-xl p-4 mb-6 overflow-x-auto">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[400px]">
            {/* Grid */}
            {[0, 25, 50, 75, 100].map((v) => {
              const x = PAD + ((W - PAD * 2) * v) / 100;
              const y = H - PAD - ((H - PAD * 2) * v) / 100;
              return (
                <g key={v}>
                  <line x1={x} y1={PAD} x2={x} y2={H - PAD} stroke="hsl(var(--border))" strokeWidth={0.5} />
                  <line x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="hsl(var(--border))" strokeWidth={0.5} />
                  <text x={x} y={H - PAD + 14} fill="hsl(var(--muted-foreground))" fontSize={9} textAnchor="middle">
                    {v}
                  </text>
                  <text x={PAD - 6} y={y + 3} fill="hsl(var(--muted-foreground))" fontSize={9} textAnchor="end">
                    {v}
                  </text>
                </g>
              );
            })}
            {/* Axis labels */}
            <text x={W / 2} y={H - 6} fill="hsl(var(--muted-foreground))" fontSize={10} textAnchor="middle">
              {"Speed ->"}
            </text>
            <text
              x={10}
              y={H / 2}
              fill="hsl(var(--muted-foreground))"
              fontSize={10}
              textAnchor="middle"
              transform={`rotate(-90, 10, ${H / 2})`}
            >
              {"Quality ->"}
            </text>

            {/* Model dots */}
            {scored.map((m) => {
              const cx = PAD + ((W - PAD * 2) * m.speed) / 100;
              const cy = H - PAD - ((H - PAD * 2) * m.quality) / 100;
              const r = m.cost_per_1k_tokens_usd === 0 ? 14 : Math.max(6, 14 - m.cost_per_1k_tokens_usd * 2000);
              const isWinner = m.id === winner.id;
              const isHovered = m.id === hover;
              return (
                <g
                  key={m.id}
                  onMouseEnter={() => setHover(m.id)}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: "pointer" }}
                >
                  {isWinner && (
                    <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke="#F59E0B" strokeWidth={2} />
                  )}
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill={COLORS[m.location] || "#9CA3AF"}
                    opacity={isHovered ? 1 : 0.75}
                    stroke={isHovered ? "#fff" : "none"}
                    strokeWidth={1.5}
                  />
                  <text x={cx} y={cy - r - 4} fill="hsl(var(--foreground))" fontSize={8} textAnchor="middle">
                    {m.display_name.replace(/ \(.*\)/, "")}
                  </text>

                  {/* Tooltip */}
                  {isHovered && (
                    <foreignObject x={cx + r + 6} y={cy - 60} width={180} height={120}>
                      <div className="bg-card border border-border rounded-lg p-2 text-xs text-foreground leading-relaxed">
                        <strong>{m.display_name}</strong>
                        <div className="text-muted-foreground">
                          {m.provider} / {LOCATION_LABELS[m.location]}
                        </div>
                        <div>
                          Quality: {m.quality} / Speed: {m.speed}
                        </div>
                        <div>
                          Cost:{" "}
                          {m.cost_per_1k_tokens_usd === 0
                            ? "Free (local GPU)"
                            : `$${m.cost_per_1k_tokens_usd.toFixed(4)}/1k tok`}
                        </div>
                        <div className="font-mono">
                          {m.tokens_per_second} tok/s / {m.context_window.toLocaleString()} ctx
                        </div>
                      </div>
                    </foreignObject>
                  )}
                </g>
              );
            })}

            {/* Legend */}
            {Object.entries(COLORS).map(([loc, color], i) => (
              <g key={loc}>
                <circle cx={W - PAD - 60} cy={PAD + i * 16} r={4} fill={color} />
                <text x={W - PAD - 52} y={PAD + i * 16 + 4} fill="hsl(var(--muted-foreground))" fontSize={9}>
                  {LOCATION_LABELS[loc]}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Bar chart rows */}
        <div className="space-y-3">
          {scored.map((m) => {
            const isWinner = m.id === winner.id;
            return (
              <div
                key={m.id}
                className={`bg-card rounded-xl p-4 border ${
                  isWinner ? "border-warning" : "border-border"
                }`}
                onMouseEnter={() => setHover(m.id)}
                onMouseLeave={() => setHover(null)}
              >
                <div className="flex justify-between items-baseline mb-3">
                  <span
                    className={`font-medium ${isWinner ? "font-bold" : ""}`}
                    style={{ color: COLORS[m.location] }}
                  >
                    {m.display_name}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {m.provider} /{" "}
                    {m.cost_per_1k_tokens_usd === 0 ? "free" : `$${m.cost_per_1k_tokens_usd.toFixed(4)}/1k`} /{" "}
                    {m.tokens_per_second} tok/s
                  </span>
                </div>
                <div className="space-y-1.5">
                  <Bar label="Quality" value={m.quality} color="bg-primary" />
                  <Bar label="Speed" value={m.speed} color="bg-success" />
                  <Bar label="Value" value={Math.round(m.costScore)} color="bg-warning" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-12 text-right">{label}</span>
      <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-mono text-muted-foreground w-6">{value}</span>
    </div>
  );
}
